export interface DayPostInput {
  id?: string;
  url?: string;
  title?: string;
  text?: string;
  publishedAt?: string;
  imageUrls?: string[];
  raw?: { contentSnippet?: string; title?: string; link?: string };
}

export interface DaySentimentInput {
  postId?: string;
  label?: string;
}

export interface StoryPair {
  topic: string;
  name?: string;
}

export interface DisplayPost {
  id: string;
  url: string;
  title: string;
  snippet: string;
  fullText?: string;
  imageDescription?: string;
  imageUrls?: string[];
  expandable?: boolean;
  publishedAt?: string;
  sentiment?: 'positive' | 'neutral' | 'negative';
  selfPraise?: boolean;
}

export interface DayStoryBoard {
  primary: StoryPair;
  secondary: StoryPair[];
  posts: DisplayPost[];
}

const GENERIC_TOPIC =
  /^(economic growth|political commentary|endorsements and support|international relations|legal proceedings|general news|controversies)/i;

const SKIP_NAME = new Set([
  'united states', 'truth social', 'white house', 'supreme court', 'new york',
  'fox news', 'donald trump', 'president trump', 'save america', 'great patriot',
  'thomas jefferson', 'george washington', 'abraham lincoln', 'barack hussein',
  'lake ontario', 'lake america', 'data center'
]);

const SKIP_LAST = new Set([
  'alongside', 'columns', 'house', 'report', 'news', 'times', 'trump', 'america',
  'states', 'social', 'court', 'hussein', 'jefferson', 'left', 'like', 'when',
  'then', 'from', 'with', 'that'
]);

const KNOWN_FIRST = new Set([
  'adam', 'alvin', 'ashley', 'barack', 'bill', 'brian', 'chuck', 'dan', 'elon',
  'eric', 'gavin', 'hakeem', 'jared', 'joe', 'john', 'kathy', 'lara', 'letitia',
  'lindsey', 'marco', 'maria', 'mark', 'melania', 'michael', 'mike', 'nancy',
  'pete', 'ro', 'ron', 'sean', 'ted', 'trey', 'tucker'
]);

const KNOWN_FIGURES = [
  'Kathy Hochul', 'Lindsey Graham', 'Michael Cohen', 'Maria Bartiromo',
  'Letitia James', 'Alvin Bragg', 'Ashley Hinson', 'Gavin Newsom', 'Marco Rubio',
  'JD Vance', 'Joe Biden', 'Barack Obama', 'Dan Sullivan', 'Trey Gowdy',
  'Ro Khanna', 'Hakeem Jeffries', 'Bill Maher', 'Mark Levin', 'Sean Hannity',
  'Melania Trump', 'Lara Trump', 'Dan Patrick', 'Ron Estes', 'Chuck Schumer'
];

function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

export function postDedupeKey(post: DayPostInput): string {
  const url = String(post.url || post.id || post.raw?.link || '');
  const status = url.match(/\/statuses\/(\d+)/);
  if (status) return status[1];
  return url.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '') || String(post.id || '');
}

export function dedupePosts<T extends DayPostInput>(posts: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const post of posts) {
    const key = postDedupeKey(post);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(post);
  }
  return out;
}

export function cleanTitle(raw: string): string {
  let t = stripHtml(raw);
  t = t.replace(/^\[No Title\].*$/i, '');
  t = t.replace(/@realDonaldTrump/gi, ' ');
  t = t.replace(/^RT\s+/i, '');
  t = t.replace(/^@[A-Za-z0-9_.]+\s*/i, '');
  t = t.replace(/https?:\/\/\S+/gi, '');
  t = t.replace(/\s*President DONALD J\.?\s*TRUMP\s*$/i, '');
  t = t.replace(/\s*President DJT\s*$/i, '');
  t = t.replace(/\s+/g, ' ').replace(/[:：]\s*$/, '').trim();
  if (/^https?:\/\//i.test(t) || t.length < 8) return '';
  return t;
}

function isCompleteHeadline(topic: string): boolean {
  const t = topic.replace(/\s+/g, ' ').trim();
  if (t.length < 12 || t.length > 140) return false;
  if (GENERIC_TOPIC.test(t)) return false;
  if (/\b(the|a|an|of|and|or|to|for|in|on|at|by|with|from|as|that|throughout|who|which|like|then|when)\s*$/i.test(t)) return false;
  if (/\bto\s+[A-Za-z][A-Za-z'-]*$/i.test(t)) return false;
  if (/\b(u\.s\.a|u\.s|lt)\.?$/i.test(t)) return false;
  if (/^https?:/i.test(t) || /^RT:?\s/i.test(t)) return false;
  return true;
}

function compactHeadline(raw: string, max = 110): string {
  const t = raw.replace(/\s+/g, ' ').trim();
  if (!t) return '';
  const dash = t.split(/\s+[—–]\s+/)[0]?.trim();
  if (dash && dash !== t && isCompleteHeadline(dash) && dash.length <= max) return dash;
  if (isCompleteHeadline(t) && t.length <= max) return t;
  if (t.length > max) {
    const slice = t.slice(0, max);
    const at = slice.lastIndexOf(' ');
    const cut = (at > 40 ? slice.slice(0, at) : slice).trim().replace(/[,:;.-]+$/, '');
    if (isCompleteHeadline(cut)) return cut;
  }
  return isCompleteHeadline(t) ? t : '';
}

function firstSentence(text: string, max: number): string {
  const t = text.replace(/\s+/g, ' ').trim();
  if (!t) return '';
  const match = t.match(/^(.+?[.!?])(\s|$)/);
  const lead = (match ? match[1] : t).trim();
  if (lead.length <= max) return lead;
  const slice = lead.slice(0, max);
  const at = slice.lastIndexOf(' ');
  return `${(at > 40 ? slice.slice(0, at) : slice).trim()}…`;
}

/** Short lead for UI lists. Stored OpenAI headlines are passed through acceptStoredHeadline instead. */
export function displayLead(raw: string, max = 120): string {
  const t = raw.replace(/\s+/g, ' ').trim();
  if (!t) return '';
  return compactHeadline(t, max) || firstSentence(t, max);
}

function extractName(text: string): string | undefined {
  const hay = stripHtml(text);
  for (const figure of KNOWN_FIGURES) {
    if (new RegExp(`\\b${figure.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(hay)) {
      return figure === 'J.D. Vance' ? 'JD Vance' : figure;
    }
  }
  const match = hay.match(/\b([A-Z][a-z]+(?:-[A-Z][a-z]+)?\s+[A-Z][a-z]+(?:-[A-Z][a-z]+)?)\b/);
  if (!match) return undefined;
  const name = match[1].replace(/\s+/g, ' ').trim();
  const key = name.toLowerCase();
  if (SKIP_NAME.has(key) || /\b(trump|donald)\b/i.test(name)) return undefined;
  const parts = name.split(/\s+/);
  const first = parts[0].toLowerCase();
  const last = parts[parts.length - 1].toLowerCase();
  if (SKIP_LAST.has(last) || !KNOWN_FIRST.has(first)) return undefined;
  return name;
}

function headlineFromPost(post: DayPostInput): string {
  const title = cleanTitle(post.title || post.raw?.title || '');
  const { body } = splitImageDescription(rawPostText(post));
  return displayLead(title) || displayLead(cleanTitle(body));
}

const IMAGE_MARKER = '[Image]';

function rawPostText(post: DayPostInput): string {
  // Enriched `[Image]` lines are written onto `text`, not the original RSS snippet.
  return post.text || post.raw?.contentSnippet || '';
}

/** Split an appended `[Image] …` catalog line from the visible post caption. */
export function splitImageDescription(raw: string): { body: string; imageDescription?: string } {
  const text = stripHtml(raw);
  if (!text) return { body: '' };
  const idx = text.lastIndexOf(IMAGE_MARKER);
  if (idx === -1) return { body: text };
  const after = text.slice(idx + IMAGE_MARKER.length).trim();
  const before = text.slice(0, idx).trim();
  if (!after) return { body: before || text };
  return { body: before, imageDescription: after };
}

function snippetFromBody(body: string): string {
  if (!body) return '';
  if (body.length <= 180) return body;
  const slice = body.slice(0, 180);
  const at = slice.lastIndexOf(' ');
  return `${(at > 80 ? slice.slice(0, at) : slice).trim()}…`;
}

function normalizeForCompare(s: string): string {
  return s
    .replace(/…/g, ' ')
    .replace(/\.{2,}/g, ' ')
    .replace(/President DONALD J\.?\s*TRUMP/gi, ' ')
    .replace(/President DJT/gi, ' ')
    .replace(/[^a-z0-9]+/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

export function isRedundantSnippet(title: string, snippet: string): boolean {
  const a = normalizeForCompare(title);
  const b = normalizeForCompare(snippet);
  if (!b) return true;
  if (!a) return false;
  if (a === b) return true;
  if (a.startsWith(b) || b.startsWith(a)) return true;
  const n = Math.min(a.length, b.length);
  return n >= 32 && a.slice(0, n) === b.slice(0, n);
}

export const UNTITLED_POST_LABEL = 'Untitled post';

const IMAGE_FILE = /\.(?:jpe?g|png|webp|gif)(?:\?|#|$)/i;

export function sanitizeImageUrls(urls: unknown): string[] {
  if (!Array.isArray(urls)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const url of urls) {
    if (typeof url !== 'string' || !/^https:\/\//i.test(url) || !IMAGE_FILE.test(url)) continue;
    if (seen.has(url)) continue;
    seen.add(url);
    out.push(url);
  }
  return out;
}

function lookupKeys(id: string): string[] {
  const value = String(id || '').trim();
  if (!value) return [];
  const keys = [value];
  const status = value.match(/\/statuses\/(\d+)/);
  if (status) keys.push(status[1]);
  const stripped = value.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '');
  if (stripped && stripped !== value) keys.push(stripped);
  return keys;
}

function selfPraiseLookup(ids: string[]): Set<string> {
  const set = new Set<string>();
  for (const id of ids) {
    for (const key of lookupKeys(id)) set.add(key);
  }
  return set;
}

export function listDayPosts(
  posts: DayPostInput[],
  sentiments: DaySentimentInput[] = [],
  selfPraisePostIds: string[] = [],
): DisplayPost[] {
  const byId = new Map<string, 'positive' | 'neutral' | 'negative'>();
  for (const s of sentiments) {
    if (!s.postId || (s.label !== 'positive' && s.label !== 'neutral' && s.label !== 'negative')) continue;
    byId.set(s.postId, s.label);
    const status = String(s.postId).match(/\/statuses\/(\d+)/);
    if (status) byId.set(status[1], s.label);
  }
  const praise = selfPraiseLookup(selfPraisePostIds);

  const out: DisplayPost[] = [];
  for (const post of dedupePosts(posts)) {
    const { body, imageDescription } = splitImageDescription(rawPostText(post));
    const cleaned = displayLead(cleanTitle(post.title || post.raw?.title || ''));
    const title = cleaned || displayLead(body) || UNTITLED_POST_LABEL;
    const snippet = snippetFromBody(body);
    const id = String(post.id || post.url || postDedupeKey(post));
    const url = String(post.url || post.raw?.link || post.id || '');
    const key = postDedupeKey(post);
    const sentiment = byId.get(id) || byId.get(key) || byId.get(url);
    const selfPraise = lookupKeys(id).some((k) => praise.has(k))
      || lookupKeys(key).some((k) => praise.has(k))
      || lookupKeys(url).some((k) => praise.has(k));
    const hasFullText = Boolean(body && normalizeForCompare(body) !== normalizeForCompare(title));
    const expandable = Boolean(
      hasFullText ||
      title.length > 140 ||
      (imageDescription && imageDescription.length > 80)
    );
    const imageUrls = sanitizeImageUrls(post.imageUrls);
    out.push({
      id,
      url,
      title,
      snippet: isRedundantSnippet(title, snippet) ? '' : snippet,
      ...(body ? { fullText: body } : {}),
      ...(imageDescription ? { imageDescription } : {}),
      ...(imageUrls.length ? { imageUrls } : {}),
      ...(expandable ? { expandable: true } : {}),
      publishedAt: post.publishedAt,
      ...(sentiment ? { sentiment } : {}),
      ...(selfPraise ? { selfPraise: true } : {})
    });
  }
  return out.sort((a, b) => String(b.publishedAt || '').localeCompare(String(a.publishedAt || '')));
}

function sameStory(a: StoryPair, b: StoryPair): boolean {
  return a.topic.trim().toLowerCase() === b.topic.trim().toLowerCase();
}

function acceptStoredHeadline(topic: string | undefined): string | undefined {
  const t = (topic || '').replace(/\s+/g, ' ').trim();
  if (!t || GENERIC_TOPIC.test(t)) return undefined;
  if (/^https?:/i.test(t) || /^RT:?\s/i.test(t)) return undefined;
  return t;
}

function openingKey(text: string): string {
  const tokens = text
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  return tokens.slice(0, 6).join(' ');
}

export function buildDayStoryBoard(input: {
  posts?: DayPostInput[];
  sentiments?: DaySentimentInput[];
  selfPraisePostIds?: string[];
  trendingTopic?: string;
  trendingName?: string;
  secondaryHighlights?: StoryPair[];
}): DayStoryBoard {
  const posts = listDayPosts(input.posts || [], input.sentiments || [], input.selfPraisePostIds || []);
  const ranked: StoryPair[] = [];
  const seen = new Set<string>();

  const push = (pair?: StoryPair) => {
    const topic = acceptStoredHeadline(pair?.topic);
    if (!topic || seen.has(topic.toLowerCase())) return;
    seen.add(topic.toLowerCase());
    ranked.push({ topic, ...(pair?.name ? { name: pair.name } : {}) });
  };

  push(
    input.trendingTopic
      ? { topic: input.trendingTopic, ...(input.trendingName ? { name: input.trendingName } : {}) }
      : undefined
  );
  const storedSecondaries = input.secondaryHighlights;
  for (const extra of storedSecondaries || []) push(extra);

  // When OpenAI already classified the day, do not pad "Also trending" with raw
  // post bodies (those are the Posts list). Local clustering is only a fallback.
  if (!Array.isArray(storedSecondaries)) {
    const clusters = new Map<string, { headlines: string[]; names: string[] }>();
    for (const post of dedupePosts(input.posts || [])) {
      const topic = headlineFromPost(post);
      if (!topic) continue;
      const name = extractName(`${cleanTitle(post.title || '')} ${post.raw?.contentSnippet || stripHtml(post.text || '')}`);
      const key = openingKey(topic) || topic.toLowerCase();
      const prev = clusters.get(key);
      if (prev) {
        prev.headlines.push(topic);
        if (name) prev.names.push(name);
      } else {
        clusters.set(key, { headlines: [topic], names: name ? [name] : [] });
      }
    }
    const clustered = Array.from(clusters.values())
      .map((group) => {
        const topic = [...group.headlines].sort((a, b) => a.length - b.length)[0];
        const unanimous = group.names.length > 0 && group.names.every((n) => n.toLowerCase() === group.names[0].toLowerCase());
        const name = unanimous ? group.names[0] : undefined;
        return {
          topic,
          count: group.headlines.length,
          ...(name ? { name } : {})
        };
      })
      .sort((a, b) => b.count - a.count);
    for (const group of clustered) push(group);
  }

  const primary = ranked[0] || { topic: '' };
  const secondary = ranked.filter((s) => !sameStory(s, primary)).slice(0, 3);
  return { primary, secondary, posts };
}
